import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CalendarIcon } from 'lucide-react'
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns'
import { fr, arSA, type Locale } from 'date-fns/locale'

export type PeriodType = 'today' | 'this_week' | 'this_month' | 'this_year' | 'last_30_days' | 'custom'

export interface DateRange {
  startDate: Date
  endDate: Date
}

interface PeriodSelectorProps {
  onPeriodChange: (range: DateRange, periodType: PeriodType) => void
  defaultPeriod?: PeriodType
}

export function PeriodSelector({ onPeriodChange, defaultPeriod = 'this_month' }: PeriodSelectorProps) {
  const { t, i18n } = useTranslation()
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>(defaultPeriod)
  const [customRange, setCustomRange] = useState<DateRange | null>(null)
  const [isCustomOpen, setIsCustomOpen] = useState(false)

  const locale = i18n.language === 'ar' ? arSA : fr

  const periods: { key: PeriodType; label: string }[] = [
    { key: 'today', label: t('accounting.period.today') },
    { key: 'this_week', label: t('accounting.period.thisWeek') },
    { key: 'this_month', label: t('accounting.period.thisMonth') },
    { key: 'this_year', label: t('accounting.period.thisYear') },
    { key: 'last_30_days', label: t('accounting.period.last30Days') },
    { key: 'custom', label: t('accounting.period.custom') },
  ]

  const getDateRange = (period: PeriodType): DateRange => {
    const now = new Date()
    switch (period) {
      case 'today':
        return { startDate: startOfDay(now), endDate: endOfDay(now) }
      case 'this_week':
        return { startDate: startOfWeek(now, { weekStartsOn: 1 }), endDate: endOfWeek(now, { weekStartsOn: 1 }) }
      case 'this_month':
        return { startDate: startOfMonth(now), endDate: endOfMonth(now) }
      case 'this_year':
        return { startDate: subMonths(now, 12), endDate: now }
      case 'last_30_days':
        return { startDate: subDays(now, 30), endDate: now }
      case 'custom':
        return customRange || { startDate: subMonths(now, 1), endDate: now }
      default:
        return { startDate: startOfMonth(now), endDate: endOfMonth(now) }
    }
  }

  const handlePeriodSelect = (period: PeriodType) => {
    setSelectedPeriod(period)
    if (period === 'custom') {
      setIsCustomOpen(true)
    } else {
      const range = getDateRange(period)
      onPeriodChange(range, period)
    }
  }

  const handleCustomDateSelect = (range: DateRange) => {
    setCustomRange(range)
    setSelectedPeriod('custom')
    onPeriodChange(range, 'custom')
    setIsCustomOpen(false)
  }


  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {periods.map((period) => (
          period.key === 'custom' ? (
            <Popover key={period.key} open={isCustomOpen} onOpenChange={setIsCustomOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={selectedPeriod === period.key ? 'default' : 'outline'}
                  size="sm"
                  className="gap-2"
                >
                  <CalendarIcon className="h-4 w-4" />
                  {period.label}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CustomDateRangePicker
                  initialRange={customRange || getDateRange('last_30_days')}
                  onSelect={handleCustomDateSelect}
                  locale={locale}
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Button
              key={period.key}
              variant={selectedPeriod === period.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePeriodSelect(period.key)}
            >
              {period.label}
            </Button>
          )
        ))}
      </div>
      {selectedPeriod === 'custom' && customRange && (
        <div className="flex items-center gap-2 px-1 py-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t('accounting.selectedRange')}:
          </span>
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-yellow-400/15 border border-yellow-400/30 px-4 py-2 shadow-sm">
            <CalendarIcon className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-semibold text-foreground">
              {format(customRange.startDate, 'dd/MM/yyyy', { locale })}
            </span>
            <span className="text-muted-foreground font-medium">→</span>
            <span className="text-sm font-semibold text-foreground">
              {format(customRange.endDate, 'dd/MM/yyyy', { locale })}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

interface CustomDateRangePickerProps {
  initialRange: DateRange
  onSelect: (range: DateRange) => void
  locale: Locale
}

function CustomDateRangePicker({ initialRange, onSelect, locale }: CustomDateRangePickerProps) {
  const { t, i18n } = useTranslation()
  const [range, setRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: initialRange.startDate,
    to: initialRange.endDate
  })
  const isRTL = i18n.language === 'ar'

  const handleApply = () => {
    if (range.from && range.to) {
      onSelect({ startDate: range.from, endDate: range.to })
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{t('accounting.startDate')}:</span>
          <span className="text-muted-foreground">
            {range.from ? format(range.from, 'dd/MM/yyyy', { locale }) : '—'}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{t('accounting.endDate')}:</span>
          <span className="text-muted-foreground">
            {range.to ? format(range.to, 'dd/MM/yyyy', { locale }) : '—'}
          </span>
        </div>
      </div>
      <Calendar
        mode="range"
        selected={range}
        onSelect={(selectedRange) => {
          if (selectedRange) {
            setRange({ from: selectedRange.from, to: selectedRange.to })
          } else {
            setRange({ from: undefined, to: undefined })
          }
        }}
        locale={locale}
        dir={isRTL ? 'rtl' : 'ltr'}
        numberOfMonths={2}
      />
      <div className="flex justify-end gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={handleApply}
          disabled={!range.from || !range.to}
        >
          {t('common.apply')}
        </Button>
      </div>
    </div>
  )
}
